import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Enums, Tables } from "@how/database";
import { supabase } from "../../lib/supabase";

export type EntitlementStatus = Enums<"entitlement_status">;
export type CatalogModule = Pick<
  Tables<"platform_modules">,
  "key" | "display_name" | "status" | "is_base" | "dependencies" | "sort_order"
>;
export type Entitlement = Pick<
  Tables<"tenant_module_entitlements">,
  "module_key" | "status" | "starts_at" | "ends_at" | "configuration" | "billing_reference"
>;

/** Every module a church can be given (beta or active in the registry), in catalog order. Planned ones cannot be. */
export function useModuleCatalog() {
  return useQuery({
    queryKey: ["module-catalog"],
    queryFn: async (): Promise<CatalogModule[]> => {
      const { data, error } = await supabase
        .from("platform_modules")
        .select("key, display_name, status, is_base, dependencies, sort_order")
        .in("status", ["beta", "active"])
        .order("sort_order");
      if (error) throw error;
      return data;
    },
  });
}

export interface EntitlementChange {
  readonly moduleKey: string;
  readonly status: EntitlementStatus;
  /** The trial's last moment. Required for a trial; ignored for every other status. */
  readonly endsAt?: string;
  /** The row as it is now, if there is one. A suspend or cancel keeps its end date. */
  readonly current: Entitlement | undefined;
}

/**
 * The end date a change writes. set_module_entitlement writes ends_at on every call, and null means no end, so a
 * suspend or cancel carries the row's end through: otherwise a suspended trial came back as an open-ended activation.
 * Activating means no end.
 */
export function endsAtFor(change: EntitlementChange): string | null {
  switch (change.status) {
    case "trial":
      return change.endsAt ?? null;
    case "suspended":
    case "canceled":
      return change.current?.ends_at ?? null;
    case "active":
      return null;
  }
}

/**
 * One change to one church's module, through set_module_entitlement (platform super admin only; it enforces the
 * dependency order and audits the change). Configuration and billing reference are not sent: the function keeps the
 * row's values when they are left out (#172), so a change made elsewhere in between is not overwritten.
 */
export function useSetEntitlement(tenantId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (change: EntitlementChange) => {
      const endsAt = endsAtFor(change);
      const { error } = await supabase.rpc("set_module_entitlement", {
        p_tenant_id: tenantId,
        p_module_key: change.moduleKey,
        p_status: change.status,
        ...(endsAt === null ? {} : { p_ends_at: endsAt }),
      });
      if (error) throw error;
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["tenant", tenantId] }),
  });
}

/** What was refused, in the words a platform admin reads. Module keys are turned into their catalog names. */
export function describeEntitlementError(error: unknown, nameOf: (key: string) => string): string {
  const e = (error ?? {}) as { code?: string; message?: string };
  const message = e.message ?? "";
  if (e.code === "23514") {
    const needs = /module (\w+) requires module (\w+) to be entitled first/.exec(message);
    if (needs?.[1] !== undefined && needs[2] !== undefined) {
      const [module, dependency] = [nameOf(needs[1]), nameOf(needs[2])];
      return `${module} needs ${dependency} first. Turn on ${dependency} for this church, then try again.`;
    }
    const neededBy = /module (\w+) is required by entitled module (\w+)/.exec(message);
    if (neededBy?.[1] !== undefined && neededBy[2] !== undefined) {
      const [module, dependent] = [nameOf(neededBy[1]), nameOf(neededBy[2])];
      return `${dependent} depends on ${module}, and this church has ${dependent}. Suspend or cancel ${dependent} first.`;
    }
    if (/is not available/.test(message)) {
      return "That module is not offered to churches yet.";
    }
  }
  if (e.code === "42501") return "Only a platform super admin can change a church's modules.";
  if (e.code === "P0002") return "That module is not in the catalog.";
  return "The change was not saved. Try again.";
}

/**
 * A date picked for a trial's end, as the last second of that day in the church's time zone: the trial ends when the
 * church's day ends, wherever the platform admin who set it is (#172, item 5). An unknown zone falls back to UTC.
 */
export function endOfChurchDay(date: string, timeZone: string): string {
  const [year = 0, month = 1, day = 1] = date.split("-").map(Number);
  const wall = Date.UTC(year, month - 1, day, 23, 59, 59);
  // The zone's offset at an instant: that instant's wall clock in the zone, read as if it were UTC, minus the instant.
  const offsetAt = (instant: number): number => {
    let parts: Intl.DateTimeFormatPart[];
    try {
      parts = new Intl.DateTimeFormat("en-US", {
        timeZone,
        hourCycle: "h23",
        year: "numeric",
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
        second: "numeric",
      }).formatToParts(instant);
    } catch {
      return 0;
    }
    const get = (type: Intl.DateTimeFormatPartTypes) =>
      Number(parts.find((p) => p.type === type)?.value ?? 0);
    const asUtc = Date.UTC(
      get("year"),
      get("month") - 1,
      get("day"),
      get("hour"),
      get("minute"),
      get("second"),
    );
    return asUtc - instant;
  };
  // Twice, so a day on which the clocks change is measured with the offset in force at its end.
  const first = wall - offsetAt(wall);
  return new Date(wall - offsetAt(first)).toISOString();
}

/** Whether a trial or activation is in force now: has_module's rule, so the page says what the church sees. */
export function isInForce(entitlement: Entitlement, now = new Date()): boolean {
  return (
    (entitlement.status === "trial" || entitlement.status === "active") &&
    new Date(entitlement.starts_at) <= now &&
    (entitlement.ends_at === null || new Date(entitlement.ends_at) > now)
  );
}
